import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHero } from "../components/FormKit";
import { PlanGrid } from "../components/PlanCards";
import { getMembershipPlans, getMembershipMe } from "../lib/hub";
import { useAuth } from "../context/AuthContext";
import { MaintenanceNotice, useMaintenance } from "../components/MaintenanceNotice";

const PUBLIC_PLAN_IDS = new Set([
  "build_your_store",
  "pro_ecommerce",
]);

const toPublicPlan = (plan) =>
  plan.id === "pro_ecommerce"
    ? {
        ...plan,
        description:
          "The complete seller membership with storefront, " +
          "catalog and approved marketplace tools.",
        features: [
          "Unlimited seller products",
          "Seller store access",
          "Seller Product Catalog",
          "Advanced storefront tools",
          "Approved marketplace integrations",
          "Creator and affiliate business tools",
        ],
      }
    : plan;

export default function Membership() {
  const [plans, setPlans] = useState(null);
  const [current, setCurrent] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { flags } = useMaintenance();
  const blocked = !!(flags.membership || flags.enrollment);

  const refreshMe = () => { if (user) getMembershipMe().then((d) => setCurrent(d.access.plan_id)).catch(() => {}); };

  useEffect(() => {
    getMembershipPlans()
      .then((data) =>
        setPlans(
          (data.plans || [])
            .filter((plan) => PUBLIC_PLAN_IDS.has(plan.id))
            .map(toPublicPlan)
        )
      )
      .catch(() => setPlans([]));
    refreshMe();
  }, [user]);

  const onJoin = async (plan) => {
    if (blocked) {
      toast.info("Applications open soon. Membership enrollment is temporarily unavailable while we finalize the platform.");
      return;
    }
    if (!user) { navigate("/login", { state: { from: "/membership" } }); return; }
    // Fail-closed: real enrollment requires the application + signed terms + payment (coming soon).
    navigate(`/apply?plan=${plan.id}`);
  };

  return (
    <div data-testid="membership-page">
      <PageHero eyebrow="TAVEN Memberships" title="Choose Your Membership"
        subtitle="Build Your Store is free with instant access. Paid seller tools remain gated until billing is approved." />
      <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-7xl py-10">
        <MaintenanceNotice area="membership" className="mb-8" />
        <div className="flex items-start gap-2 bg-taven-gold/10 border border-taven-gold/40 px-4 py-3 mb-8 text-sm" data-testid="membership-billing-notice">
          <p className="text-black/70">
            <strong>Pro E-Commerce Membership</strong> includes advanced
            seller tools, storefront management and approved marketplace
            integrations.
          </p>
        </div>
        {plans === null ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-96 bg-black/5 animate-pulse" />)}</div>
        ) : (
          <PlanGrid plans={plans} current={current} onJoin={onJoin} joinDisabled={blocked} joinLabel={blocked ? "Coming Soon" : "Apply"} />
        )}
      </div>
    </div>
  );
}
